import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Grid,
  Typography,
  Button,
  TextField,
  Checkbox,
  FormControlLabel,
  Alert,
  Box,
  CircularProgress,
  Divider,
  Paper,
  Popover,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import { useApiGet, useApiSend } from '../../../utils/httpClientTs';
import {
  getLocalChapterRosterDuesDetail,
  getLocalChapterRosterDuesSummary,
  getLocalChapterRosterDuesPDF,
  approveRosterDuesForm,
  rejectRosterDuesForm,
} from '../../../endpoints/rosterDues';
import AlertItem from '../../../Components/AlertItem';
import {
  RosterDuesDetail,
  RosterDuesSummary as RosterDuesSummaryType,
} from '../../../types/rosterDues';
import { REPORT_ID_MEMBERSHIP_YEAR_FINAL } from '../../../types/peoReports';
import { formTypeToStatusArrayMap } from '../../../utils/variables';
import dayjs from 'dayjs';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';

// Format currency helper
const formatCurrency = (value: number): string => {
  if (value === null || value === undefined || isNaN(value)) {
    return '$0.00';
  }
  return `$${value.toFixed(2).replace(/\d(?=(\d{3})+\.)/g, '$&,')}`;
};

// Component for showing member list in popover
const MembersList: React.FC<{
  members: Array<{ id: string; name: string }>;
}> = ({ members }) => {
  return (
    <Box sx={{ p: 2, maxWidth: 300 }}>
      <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 'bold' }}>
        Members:
      </Typography>
      {members.map((member) => (
        <Box key={member.id} sx={{ mb: 1 }}>
          <Typography
            component="a"
            href={`/user/${member.id}`}
            target="_blank"
            rel="noopener noreferrer"
            variant="body2"
            sx={{
              color: 'primary.main',
              textDecoration: 'underline',
              cursor: 'pointer',
              '&:hover': {
                color: 'primary.dark',
              },
            }}
          >
            {member.name}
          </Typography>
        </Box>
      ))}
    </Box>
  );
};

const RosterDuesSummary: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const currentUserId = localStorage.getItem('user_id');
  const userFullName =
    `${localStorage.getItem('first_name') || ''} ${localStorage.getItem('last_name') || ''}`.trim();

  const [detail, setDetail] = useState<RosterDuesDetail | null>(null);
  const [summary, setSummary] = useState<RosterDuesSummaryType | null>(null);
  const [notesError, setNotesError] = useState(false);
  const [notesErrorDescription, setNotesErrorDescription] = useState('');
  const [signatureError, setSignatureError] = useState(false);
  const [checkboxError, setCheckboxError] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [initiatedAnchorEl, setInitiatedAnchorEl] =
    useState<HTMLElement | null>(null);
  const [reinstatedAnchorEl, setReinstatedAnchorEl] =
    useState<HTMLElement | null>(null);
  const [pdfLoading, setPdfLoading] = useState(false);

  // Modal states
  const [rejectModalOpen, setRejectModalOpen] = useState(false);
  const [approveModalOpen, setApproveModalOpen] = useState(false);
  const [modalNotes, setModalNotes] = useState('');
  const [modalSignature, setModalSignature] = useState('');
  const [modalUnderstandChecked, setModalUnderstandChecked] = useState(false);

  const {
    data: detailData,
    isLoading: detailLoading,
    refetch: refetchDetail,
  } = useApiGet(
    ['local-chapter-roster-dues-detail', id],
    () => getLocalChapterRosterDuesDetail(id!),
    {
      enabled: !!id,
      retry: 1,
      refetchOnMount: true,
      refetchOnWindowFocus: false,
    }
  );

  const {
    data: summaryData,
    isLoading: summaryLoading,
    refetch: refetchSummary,
  } = useApiGet(
    ['local-chapter-roster-dues-summary', id],
    () => getLocalChapterRosterDuesSummary(id!),
    {
      enabled: !!id,
      retry: 1,
      refetchOnMount: true,
      refetchOnWindowFocus: false,
    }
  );

  useEffect(() => {
    // Clear state when id changes
    setDetail(null);
    setSummary(null);
    setShowSuccess(false);
    setSuccessMessage('');
    setNotesError(false);
    setNotesErrorDescription('');
    setSignatureError(false);
    setCheckboxError(false);
    setRejectModalOpen(false);
    setApproveModalOpen(false);
    setModalNotes('');
    setModalSignature('');
    setModalUnderstandChecked(false);
  }, [id]);

  // Refetch data when component mounts or when navigating back to ensure fresh data
  useEffect(() => {
    if (id) {
      refetchDetail();
      refetchSummary();
    }
  }, [id, refetchDetail, refetchSummary]);

  useEffect(() => {
    if (detailData) {
      setDetail(detailData);
    }
  }, [detailData]);

  useEffect(() => {
    if (summaryData) {
      setSummary(summaryData);
    }
  }, [summaryData]);

  const canEdit = detail !== null && detail.assigned_to_id === currentUserId;

  // Form year (membership year): March = start; previous year range for initiation/reinstatement
  const d = new Date();
  const formYear = d.getMonth() >= 2 ? d.getFullYear() : d.getFullYear() - 1;
  const year = detail?.year ?? formYear;
  const prevYearRange = `03/01/${year - 1} to 02/28/${year}`;

  // Calculate totals
  const internationalDuesTotal = summary
    ? summary.active_members *
      (summary.dues_amount + summary.cottey_college_amount)
    : 0;
  const initiationTotal = summary
    ? summary.initiated * summary.initiation_fee
    : 0;
  const reinstatementTotal = summary
    ? summary.reinstated * summary.reinstated_fee
    : 0;
  const otherFeesTotal = summary
    ? (summary.other_fees ?? []).reduce((sum, fee) => {
        return (
          sum +
          (fee.per_member_fee * summary.active_members + fee.per_chapter_fee)
        );
      }, 0)
    : 0;
  const totalRemittance =
    (summary?.states_dues || 0) * (summary?.active_members || 0) +
    internationalDuesTotal +
    initiationTotal +
    reinstatementTotal +
    (summary?.tech_fee || 0) +
    otherFeesTotal;

  const { mutate: approveForm, isPending: isApproving } = useApiSend(
    (params: { formId: string; data: any }) => {
      return approveRosterDuesForm(params.formId, params.data);
    },
    () => {
      setApproveModalOpen(false);
      setShowSuccess(true);
      setSuccessMessage('Form approved successfully!');
      // Scroll to top to show success message
      window.scrollTo({ top: 0, behavior: 'smooth' });
      setTimeout(() => {
        navigate('/annual-reports');
      }, 2500);
    },
    (error) => {
      console.error('Failed to approve form:', error);
      setNotesError(true);
      setNotesErrorDescription(
        'Error approving form. If the problem persists, please contact support.'
      );
    },
    [
      ['local-chapter-roster-dues-detail', id],
      ['local-chapter-roster-dues-summary', id],
      ['roster-dues-list'],
    ]
  );

  const { mutate: rejectForm, isPending: isRejecting } = useApiSend(
    (params: { formId: string; data: any }) => {
      return rejectRosterDuesForm(params.formId, params.data);
    },
    () => {
      setRejectModalOpen(false);
      setShowSuccess(true);
      setSuccessMessage('Form rejected successfully!');
      // Scroll to top to show success message
      window.scrollTo({ top: 0, behavior: 'smooth' });
      setTimeout(() => {
        navigate('/annual-reports');
      }, 2500);
    },
    (error) => {
      console.error('Failed to reject form:', error);
      setNotesError(true);
      setNotesErrorDescription(
        'Error rejecting form. If the problem persists, please contact support.'
      );
    },
    [
      ['local-chapter-roster-dues-detail', id],
      ['local-chapter-roster-dues-summary', id],
      ['roster-dues-list'],
    ]
  );

  // Determine if rejection button should be hidden
  const shouldHideRejectionButton = (): boolean => {
    if (!detail?.status_id) return false;

    const statusArray = formTypeToStatusArrayMap['roster_lc'];

    if (!statusArray || statusArray.length === 0) return false;

    const firstStatusId = statusArray[0].id;
    return detail.status_id === firstStatusId;
  };

  const handleRejectClick = () => {
    setModalNotes('');
    setNotesError(false);
    setNotesErrorDescription('');
    setRejectModalOpen(true);
  };

  const handleApproveClick = () => {
    setModalSignature('');
    setModalUnderstandChecked(false);
    setSignatureError(false);
    setCheckboxError(false);
    setApproveModalOpen(true);
  };

  const handleApprove = () => {
    if (!id || !summary) return;

    let valid = true;

    if (!modalUnderstandChecked) {
      setCheckboxError(true);
      valid = false;
    }

    if (modalSignature !== userFullName) {
      setSignatureError(true);
      valid = false;
    }

    if (!valid) return;

    approveForm({
      formId: id,
      data: {
        signature: modalSignature,
        understand_checked: modalUnderstandChecked,
        remit_total: totalRemittance,
        active_members: summary.active_members,
        reinstated_members: summary.reinstated,
        initiated_members: summary.initiated,
        tech_total: summary.tech_fee,
        others_total: otherFeesTotal,
      },
    });
  };

  const handleReject = () => {
    if (!id) return;

    let valid = true;

    if (modalNotes.length === 0) {
      setNotesError(true);
      setNotesErrorDescription('Notes are required for rejection');
      valid = false;
    } else if (modalNotes.length > 4000) {
      setNotesError(true);
      setNotesErrorDescription('Notes must be less than 4000 characters');
      valid = false;
    }

    if (!valid) return;

    rejectForm({
      formId: id,
      data: {
        note: modalNotes,
      },
    });
  };

  if (detailLoading || summaryLoading) {
    return (
      <Grid container padding={4} justifyContent="center">
        <CircularProgress />
      </Grid>
    );
  }

  if (!detail || !summary) {
    return (
      <Grid container padding={4}>
        <AlertItem
          severity="error"
          description="Failed to load form data."
          onClose={() => {}}
          keepOpen={true}
        />
      </Grid>
    );
  }

  return (
    <Grid container paddingLeft={2} paddingRight={2}>
      {showSuccess && (
        <Grid item xs={12} padding={2}>
          <Alert severity="success">{successMessage}</Alert>
        </Grid>
      )}

      <Grid item xs={12} padding={1}>
        <Typography variant="h4" color={'primary'}>
          {detail.local_chapter_name}
        </Typography>
        <Typography variant="body2" color={'text.secondary'} sx={{ mt: 1 }}>
          Status: {detail.status_name}
        </Typography>
      </Grid>

      {/* Action Buttons - PDF and Final Roster */}
      <Grid item xs={12} padding={1} paddingBottom={2}>
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
          <Button
            variant="contained"
            disabled={pdfLoading || !id}
            onClick={async () => {
              if (!id) return;
              setPdfLoading(true);
              try {
                const blob = await getLocalChapterRosterDuesPDF(id);
                const url = URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url;
                a.download =
                  `roster-dues-${detail?.local_chapter_name ?? id}.pdf`.replace(
                    /[^a-zA-Z0-9._-]/g,
                    '_'
                  );
                document.body.appendChild(a);
                a.click();
                document.body.removeChild(a);
                URL.revokeObjectURL(url);
              } catch (err) {
                console.error('Failed to generate Dues Form PDF', err);
              } finally {
                setPdfLoading(false);
              }
            }}
            sx={{ minWidth: 120 }}
          >
            {pdfLoading ? (
              <CircularProgress size={24} color="inherit" />
            ) : (
              'Dues Form'
            )}
          </Button>
          <Button
            variant="contained"
            onClick={() =>
              window.open(
                `/peo-reports/${REPORT_ID_MEMBERSHIP_YEAR_FINAL}`,
                '_blank'
              )
            }
          >
            Final Roster
          </Button>
        </Box>
      </Grid>

      {!canEdit && (
        <Grid item xs={12} padding={2}>
          <Alert severity="info">
            You are viewing this form in read-only mode. Only the assigned user
            can make changes.
          </Alert>
        </Grid>
      )}

      <Grid item xs={12} paddingBottom={2}>
        <Paper sx={{ p: 3 }}>
          {/* Number of Active Members */}
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={12}>
              <Typography variant="body1" fontWeight="medium">
                Number of Active Members as of March 1, {year}:{' '}
                {summary.active_members}
              </Typography>
            </Grid>
          </Grid>

          <Divider sx={{ my: 3 }} />

          {/* Section I: Dues */}
          <Grid container spacing={2} sx={{ mb: 2 }}>
            <Grid item xs={12}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography variant="body1" fontWeight="bold">
                  I. Dues payable to {summary.check_payable_to} per active
                  member:
                </Typography>
                <Tooltip title="The total amount of S/P/D and International dues for each member.">
                  <InfoOutlinedIcon color="info" fontSize="small" />
                </Tooltip>
              </Box>
            </Grid>
            <Grid item xs={9}>
              <Typography variant="body1">
                S/P/D Chapter dues of ${(summary.states_dues || 0).toFixed(2)}:
              </Typography>
            </Grid>
            <Grid item xs={3} textAlign="right">
              <Typography variant="body1">
                {formatCurrency(
                  (summary.states_dues || 0) * summary.active_members
                )}
              </Typography>
            </Grid>
            <Grid item xs={9}>
              <Typography variant="body1">
                International Chapter dues of $
                {(summary.dues_amount + summary.cottey_college_amount).toFixed(
                  2
                )}{' '}
                (includes ${summary.cottey_college_amount.toFixed(2)} for Cottey
                College):
              </Typography>
            </Grid>
            <Grid item xs={3} textAlign="right">
              <Typography variant="body1">
                {formatCurrency(internationalDuesTotal)}
              </Typography>
            </Grid>
          </Grid>

          <Divider sx={{ my: 3 }} />

          {/* Section II: Initiation and Reinstatement Fees */}
          <Grid container spacing={2} sx={{ mb: 2 }}>
            <Grid item xs={12}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography variant="body1" fontWeight="bold">
                  II. Initiation and reinstatement fees:
                </Typography>
                <Tooltip title="The initiation and reinstatement fees for each member. Hovering over the i bubble shows which members are associated with these fees.">
                  <InfoOutlinedIcon color="info" fontSize="small" />
                </Tooltip>
              </Box>
            </Grid>
            <Grid item xs={9}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography variant="body1">
                  ${summary.initiation_fee.toFixed(2)} for each of{' '}
                  {summary.initiated} members initiated {prevYearRange}:
                </Typography>
                {summary.initiated > 0 &&
                  summary.initiated_members &&
                  summary.initiated_members.length > 0 && (
                    <>
                      <Box
                        component="span"
                        sx={{ display: 'inline-flex', cursor: 'pointer' }}
                        onClick={(e) => setInitiatedAnchorEl(e.currentTarget)}
                      >
                        <InfoOutlinedIcon color="primary" fontSize="small" />
                      </Box>
                      <Popover
                        open={Boolean(initiatedAnchorEl)}
                        anchorEl={initiatedAnchorEl}
                        onClose={() => setInitiatedAnchorEl(null)}
                        anchorOrigin={{
                          vertical: 'bottom',
                          horizontal: 'left',
                        }}
                      >
                        <MembersList members={summary.initiated_members} />
                      </Popover>
                    </>
                  )}
              </Box>
            </Grid>
            <Grid item xs={3} textAlign="right">
              <Typography variant="body1">
                {formatCurrency(initiationTotal)}
              </Typography>
            </Grid>
            <Grid item xs={9}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography variant="body1">
                  ${summary.reinstated_fee.toFixed(2)} for each of{' '}
                  {summary.reinstated} members reinstated {prevYearRange}:
                </Typography>
                {summary.reinstated > 0 &&
                  summary.reinstated_members &&
                  summary.reinstated_members.length > 0 && (
                    <>
                      <Box
                        component="span"
                        sx={{ display: 'inline-flex', cursor: 'pointer' }}
                        onClick={(e) => setReinstatedAnchorEl(e.currentTarget)}
                      >
                        <InfoOutlinedIcon color="primary" fontSize="small" />
                      </Box>
                      <Popover
                        open={Boolean(reinstatedAnchorEl)}
                        anchorEl={reinstatedAnchorEl}
                        onClose={() => setReinstatedAnchorEl(null)}
                        anchorOrigin={{
                          vertical: 'bottom',
                          horizontal: 'left',
                        }}
                      >
                        <MembersList members={summary.reinstated_members} />
                      </Popover>
                    </>
                  )}
              </Box>
            </Grid>
            <Grid item xs={3} textAlign="right">
              <Typography variant="body1">
                {formatCurrency(reinstatementTotal)}
              </Typography>
            </Grid>
          </Grid>

          <Divider sx={{ my: 3 }} />

          {/* Section III: Tech Fee */}
          <Grid container spacing={2} sx={{ mb: 2 }}>
            <Grid item xs={9}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography variant="body1" fontWeight="bold">
                  III. Supply/resource/technology payment:
                </Typography>
                <Tooltip title="The supply/resource/technology payment fee that applies to each chapter.">
                  <InfoOutlinedIcon color="info" fontSize="small" />
                </Tooltip>
              </Box>
            </Grid>
            <Grid item xs={3} textAlign="right">
              <Typography variant="body1">
                {formatCurrency(summary.tech_fee)}
              </Typography>
            </Grid>
          </Grid>

          <Divider sx={{ my: 3 }} />

          {/* Section IV: Other Items */}
          <Grid container spacing={2} sx={{ mb: 2 }}>
            <Grid item xs={12}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography variant="body1" fontWeight="bold">
                  IV. Other Items due (refer to {summary.check_payable_to}{' '}
                  bylaws):
                </Typography>
                <Tooltip title="Outlines S/P/D dues assessments.">
                  <InfoOutlinedIcon color="info" fontSize="small" />
                </Tooltip>
              </Box>
            </Grid>
            {(summary.other_fees ?? []).length > 0 ? (
              (summary.other_fees ?? [])
                .sort((a, b) => a.order - b.order)
                .map((fee, index) => {
                  const feeAmount =
                    fee.per_member_fee > 0
                      ? fee.per_member_fee * summary.active_members
                      : fee.per_chapter_fee;
                  return (
                    <React.Fragment key={index}>
                      <Grid item xs={9}>
                        <Typography variant="body1">
                          {fee.description} (
                          {fee.per_member_fee > 0
                            ? `$${fee.per_member_fee.toFixed(2)} per member`
                            : `$${fee.per_chapter_fee.toFixed(2)} per chapter`}
                          ):
                        </Typography>
                      </Grid>
                      <Grid item xs={3} textAlign="right">
                        <Typography variant="body1">
                          {formatCurrency(feeAmount)}
                        </Typography>
                      </Grid>
                    </React.Fragment>
                  );
                })
            ) : (
              <>
                <Grid item xs={9}>
                  <Typography variant="body1"></Typography>
                </Grid>
                <Grid item xs={3} textAlign="right">
                  <Typography variant="body1">{formatCurrency(0)}</Typography>
                </Grid>
              </>
            )}
          </Grid>
          <Divider sx={{ my: 3 }} />

          {/* Section V: Total Remittance */}
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={9}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography variant="body1" fontWeight="bold">
                  V. Total remittance:
                </Typography>
                <Tooltip title="The total required remittance.">
                  <InfoOutlinedIcon color="info" fontSize="small" />
                </Tooltip>
              </Box>
            </Grid>
            <Grid item xs={3} textAlign="right">
              <Typography variant="body1" fontWeight="bold">
                {formatCurrency(totalRemittance)}
              </Typography>
            </Grid>
          </Grid>

          {/* Remittance Address */}
          <Box
            sx={{ mt: 4, p: 2, backgroundColor: 'grey.50', borderRadius: 1 }}
          >
            <Typography variant="body2" sx={{ mb: 1 }}>
              Send check for total remittance due along with a copy of this form
              to the address shown below. Send a separate check and project
              donations form for project gifts.
            </Typography>
            {summary.check_payable_to && (
              <Typography variant="body1" sx={{ mt: 1 }}>
                Check Payable To: {summary.check_payable_to}
              </Typography>
            )}
            <Typography variant="body1" fontWeight="medium" sx={{ mt: 2 }}>
              Remittance Address:
            </Typography>
            <Typography variant="body1">{summary.remit_to_name}</Typography>
            <Typography variant="body1">{summary.address_1}</Typography>
            {summary.address_2 && (
              <Typography variant="body1">{summary.address_2}</Typography>
            )}
            <Typography variant="body1">
              {summary.city}, {summary.state_abbreviation} {summary.postal_code}
            </Typography>
            <Typography variant="body1">
              {summary.country_abbreviation}
            </Typography>
          </Box>
        </Paper>
      </Grid>

      {/* Previous Notes - Always visible */}
      {summary.notes && summary.notes.length > 0 && (
        <Grid item xs={12} paddingTop={2}>
          <Typography variant="body1" fontWeight="bold" sx={{ mb: 1 }}>
            Previous Notes:
          </Typography>
          {summary.notes.map((note) => (
            <Grid
              container
              key={note.id}
              sx={{ mb: 2, pb: 2, borderBottom: '1px solid #e0e0e0' }}
            >
              <Grid item xs={12} sm={12} md={5} lg={4}>
                <Typography variant="body2" color="text.secondary">
                  {note.name} on{' '}
                  {dayjs(note.created_on).format('MM/DD/YYYY hh:mm A')}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={12} md={7} lg={8}>
                <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                  {note.note}
                </Typography>
              </Grid>
            </Grid>
          ))}
        </Grid>
      )}

      {/* Action Buttons - Only visible to assigned user */}
      {canEdit && (
        <Grid item xs={12} paddingTop={4} paddingBottom={2}>
          {detail?.can_submit_approval === false &&
            detail?.membership_year_start && (
              <Alert severity="info" sx={{ mb: 2 }}>
                Approvals can be submitted starting{' '}
                {dayjs(detail.membership_year_start).format('MMMM D, YYYY')}.
              </Alert>
            )}
          <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
            <Button
              variant="outlined"
              onClick={() => navigate('/annual-reports/roster-dues')}
              disabled={isApproving || isRejecting}
            >
              Back to List
            </Button>
            <Button
              variant="outlined"
              onClick={() =>
                navigate(`/annual-reports/roster-dues/${id}/members`)
              }
              disabled={isApproving || isRejecting}
            >
              Back to Members
            </Button>
            {!shouldHideRejectionButton() && (
              <Button
                variant="contained"
                color="error"
                onClick={handleRejectClick}
                disabled={
                  !canEdit ||
                  isApproving ||
                  isRejecting ||
                  detail?.can_submit_approval === false
                }
              >
                SUBMIT AS REJECTED
              </Button>
            )}
            <Button
              variant="contained"
              color="primary"
              onClick={handleApproveClick}
              disabled={
                !canEdit ||
                isApproving ||
                isRejecting ||
                detail?.can_submit_approval === false
              }
            >
              SUBMIT AS APPROVED
            </Button>
          </Box>
        </Grid>
      )}

      {/* Back buttons for non-assigned users */}
      {!canEdit && (
        <Grid item xs={12} paddingTop={4} paddingBottom={2}>
          <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
            <Button
              variant="outlined"
              onClick={() => navigate('/annual-reports/roster-dues')}
            >
              Back to List
            </Button>
            <Button
              variant="outlined"
              onClick={() =>
                navigate(`/annual-reports/roster-dues/${id}/members`)
              }
            >
              Back to Roster
            </Button>
          </Box>
        </Grid>
      )}

      {/* Rejection Modal */}
      <Dialog
        open={rejectModalOpen}
        onClose={() => !isRejecting && setRejectModalOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Submit as Rejection</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2, mt: 1 }}>
            Please explain why you are rejecting this form. This note is
            required.
          </Typography>
          <TextField
            id="rejection-notes"
            label="Explanation / Notes *"
            multiline
            rows={6}
            fullWidth
            value={modalNotes}
            onChange={(e) => {
              const value = e.target.value;
              setModalNotes(value);
              if (value.length > 4000) {
                setNotesError(true);
                setNotesErrorDescription(
                  'Notes must be less than 4000 characters'
                );
              } else if (value.length > 0) {
                setNotesError(false);
                setNotesErrorDescription('');
              }
            }}
            error={notesError}
            helperText={
              notesErrorDescription || 'Notes are required for rejection'
            }
            disabled={isRejecting}
            required
          />
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setRejectModalOpen(false)}
            disabled={isRejecting}
          >
            Cancel
          </Button>
          <Button
            onClick={handleReject}
            variant="contained"
            color="error"
            disabled={isRejecting}
            startIcon={
              isRejecting ? (
                <CircularProgress size={16} color="inherit" />
              ) : null
            }
          >
            {isRejecting ? 'SUBMITTING...' : 'Submit Rejection'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Approval Modal */}
      <Dialog
        open={approveModalOpen}
        onClose={() => !isApproving && setApproveModalOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Submit as Approved</DialogTitle>
        <DialogContent>
          <FormControlLabel
            control={
              <Checkbox
                checked={modalUnderstandChecked}
                onChange={(e) => {
                  setModalUnderstandChecked(e.target.checked);
                  setCheckboxError(false);
                }}
                disabled={isApproving}
                sx={checkboxError ? { color: 'error.main' } : {}}
              />
            }
            label="I hereby attest that the information submitted and the Roster and Dues Form are accurate and balanced.*"
            sx={{ mt: 2, mb: 2 }}
          />
          {checkboxError && (
            <Typography
              variant="caption"
              color="error"
              sx={{ display: 'block', mb: 2 }}
            >
              This checkbox must be checked to submit.
            </Typography>
          )}

          <Typography variant="body2" sx={{ mb: 1, mt: 2 }}>
            Electronic Signature:
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Please type your name in the box. The name you enter must match the
            name shown below the box, including punctuation and spacing.
          </Typography>
          <TextField
            id="approval-signature"
            label="Name*"
            fullWidth
            value={modalSignature}
            onChange={(e) => {
              setModalSignature(e.target.value);
              if (e.target.value === userFullName) {
                setSignatureError(false);
              }
            }}
            error={signatureError}
            helperText={
              signatureError ? 'Name must match the name shown below' : ''
            }
            disabled={isApproving}
            sx={{ mb: 1 }}
          />
          <Typography variant="body2" color="text.secondary">
            {userFullName}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setApproveModalOpen(false)}
            disabled={isApproving}
          >
            Cancel
          </Button>
          <Button
            onClick={handleApprove}
            variant="contained"
            color="primary"
            disabled={isApproving}
            startIcon={
              isApproving ? (
                <CircularProgress size={16} color="inherit" />
              ) : null
            }
          >
            {isApproving ? 'SUBMITTING...' : 'Submit Approval'}
          </Button>
        </DialogActions>
      </Dialog>
    </Grid>
  );
};

export default RosterDuesSummary;
